import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { scheduleRequests } from "../../../../store/remediation/remediationSlice";
import { friendlyErrorMessage, friendlySkippedReason } from "../common/ErrorCodeMap";
import { useRemediationSnackbar } from "../common/SnackbarHost";
import { useActionFeedback } from "../common/hooks";

const toLocalInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch (_e) { return ""; }
};

const fromLocalInput = (val) => {
  if (!val) return null;
  try { return new Date(val).toISOString(); } catch (_e) { return null; }
};

/**
 * Re-schedule modal — used in both single and bulk modes.
 * Drives POST /remediation/requests/schedule.
 */
const RescheduleModal = ({ open, requestIds = [], onClose, onDone }) => {
  const dispatch = useDispatch();
  const { showSnackbar } = useRemediationSnackbar();
  const notifSettings = useSelector((s) => s.remediation.notificationSettings.data);

  const [scheduledAt, setScheduledAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const noticeMinutes = notifSettings?.pre_execution_notice_minutes ?? 30;
  const minDt = new Date(Date.now() + noticeMinutes * 60 * 1000);
  const maxDt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  useEffect(() => {
    if (open) {
      // Default to 1 hour from now, rounded to the next 15 mins.
      const def = new Date(Date.now() + Math.max(60, noticeMinutes + 30) * 60 * 1000);
      def.setSeconds(0); def.setMilliseconds(0);
      setScheduledAt(def.toISOString());
      setError(null);
    }
  }, [open, noticeMinutes]);

  const handleSubmit = () => {
    if (!scheduledAt) { setError("Pick a date and time."); return; }
    if (new Date(scheduledAt) < minDt) {
      setError(`Time must be at least ${noticeMinutes} minutes from now.`);
      return;
    }
    if (new Date(scheduledAt) > maxDt) {
      setError("Time can't be more than 30 days out.");
      return;
    }
    setSubmitting(true);
    dispatch(scheduleRequests({ request_ids: requestIds, scheduled_at: scheduledAt }));
  };

  // Only react to schedule completions originating while this modal is open.
  useActionFeedback("schedule", ({ succeeded = [], skipped = [], error }) => {
    if (!open) return;
    setSubmitting(false);
    if (error) {
      showSnackbar({ message: friendlyErrorMessage(error), severity: "error" });
      return;
    }
    if (succeeded.length && skipped.length === 0) {
      showSnackbar({
        message: `Re-scheduled ${succeeded.length} request${succeeded.length === 1 ? "" : "s"}.`,
        severity: "success",
      });
    } else if (succeeded.length && skipped.length) {
      showSnackbar({
        message: `Re-scheduled ${succeeded.length}, skipped ${skipped.length}.`,
        severity: "warning",
        title: "Partial success",
        details: skipped.map((s) => `#${s.request_id}: ${friendlySkippedReason(s)}`),
      });
    } else {
      showSnackbar({
        message: "Couldn't re-schedule.",
        severity: "error",
        details: skipped.map((s) => `#${s.request_id}: ${friendlySkippedReason(s)}`),
      });
    }
    if (typeof onDone === "function") onDone();
    onClose();
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {requestIds.length > 1
          ? `Re-schedule ${requestIds.length} requests`
          : "Re-schedule request"}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          {requestIds.length > 1
            ? `Sets a new scheduled time for ${requestIds.length} requests. Rows already running or terminal will come back skipped.`
            : "Pick a new time to run this remediation."}
        </Typography>

        <TextField
          type="datetime-local"
          size="small"
          label="Run at"
          value={toLocalInput(scheduledAt)}
          onChange={(e) => { setScheduledAt(fromLocalInput(e.target.value)); setError(null); }}
          inputProps={{
            min: toLocalInput(minDt.toISOString()),
            max: toLocalInput(maxDt.toISOString()),
          }}
          InputLabelProps={{ shrink: true }}
          fullWidth
          error={Boolean(error)}
          helperText={
            error ||
            `Between ${minDt.toLocaleString()} and ${maxDt.toLocaleString()}.`
          }
          sx={{ mb: 1.5 }}
        />

        {requestIds.length > 1 && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Bulk action: every selected request will be re-scheduled to the same time.
            Per-row results show up in the toast.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {submitting ? "Saving…" : "Re-schedule"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RescheduleModal;
